import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ReportApiType,
  ReportInputModel,
  ReportListModel,
  ReportOutputModel,
  ReportResult
} from '../../../../../../shared/models/report.model';
import { ReportService } from '../../../../../../shared/service/report.service';
import { SubSink } from 'subsink';
import { HttpError } from '../../../../../../shared/models/http-error.model';
import Swal from 'sweetalert2';
import { TransferMode } from '../../../../../../shared/models/withdraw.model';
import { TimeAgoPipe } from '../../../../../../shared/pipes/time-ago.pipe';
import { NbDialogService } from '@nebular/theme';
import Chart, { ChartTypeRegistry } from 'chart.js/auto';


declare const prettyPrint: any;

@Component({
  selector: 'app-report-page',
  templateUrl: './report-page.component.html',
  styleUrls: ['./report-page.component.scss'],
  providers: [TimeAgoPipe]
})
export class ReportPageComponent implements OnInit, OnDestroy {

  @ViewChild('JSONOutputTemplate') JSONOutputTemplate!: TemplateRef<any>;
  @ViewChild('ExcelResultPasswordTemplate') ExcelResultPasswordTemplate!: TemplateRef<any>;
  @ViewChild('ReportApiFormModalTemplate') ReportApiFormModalTemplate!: TemplateRef<any>;
  private _sub = new SubSink();
  private reportId!: string;
  getReportInputLoading = false;
  reportFilters: ReportInputModel[] = [];
  private Toast = Swal.mixin({
    toast: true,
    position: 'top',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
  });
  report!: ReportListModel;
  reportOutput!: ReportOutputModel[];
  reportFilter: any = {};
  reportApiFilter: any = {};
  reportResult: ReportResult[] = [];
  validationError: any = {};
  getResultLoading = false;
  reportOutputType = 'table';
  excelResultPassword!: string;
  selectedReportApi!: ReportApiType;
  charts: Chart[] = [];

  protected readonly transferMode = TransferMode;
  sendReportApiRequestLoading = false;
  selectedReportApiOutput!: ReportOutputModel;

  constructor(private route: ActivatedRoute, private reportService: ReportService, private router: Router, private dialogService: NbDialogService) {
    Chart.defaults.font.family = `'IRANSans_FaNum', Tahoma, Arial, 'Roboto', sans-serif`;
    Chart.defaults.color = '#ffffff';
  }


  copyPassword() {
    navigator.clipboard.writeText(this.excelResultPassword);
    this.Toast.fire({ icon: 'success', text: 'پسورد فایل گزارش با موفقیت کپی شد.' });

  }

  getSingleReport() {
    this._sub.sink = this.reportService.getSingleReport(this.reportId).subscribe(report => {
      this.report = report;
      this.reportOutputType = this.report.supportedOutputs[0];
    });
  }

  getReportOutput() {
    this._sub.sink = this.reportService.getReportOutput(this.reportId).subscribe(output => {
      this.reportOutput = output;
    });
  }

  openJsonViewerDialog(output: any) {
    this.dialogService.open(this.JSONOutputTemplate);
    const element = document.getElementById('json-output-result');
    const converted = prettyPrint(output);
    if (element) {
      element.appendChild(converted);
    }
  }

  getReportResult() {
    this.reportFilter['__type'] = this.reportOutputType;
    this.charts.map(chart => chart.destroy());
    this.getResultLoading = true;
    this._sub.sink = this.reportService.getReportResult(this.reportId, this.reportFilter).subscribe((result) => {
      if (this.reportOutputType === 'excel') {
        this.excelResultPassword = (result as any).password;
        this.dialogService.open(this.ExcelResultPasswordTemplate);
      }
      if (this.reportOutputType === 'chart') {
        setTimeout(() => {
          this.renderCharts();
        }, 10);
      }
      this.reportResult = result;
      this.getResultLoading = false;
    }, (response: HttpError) => {
      this.getResultLoading = false;
      if (response.error) {
        this.Toast.fire({ icon: 'error', text: response.error });
      }
    });
  }

  renderCharts() {
    for (const chart of this.reportResult) {
      const element = document.getElementById(chart.id as string) as HTMLCanvasElement | null;
      if (element) {
        const chartType = chart.chartType === 'stack' ? 'bar' : chart.chartType;
        const scale = {
          x: {
            stacked: true
          },
          y: {
            stacked: true
          }
        };
        this.charts.push(new Chart(element,
          {
            type: chartType as keyof ChartTypeRegistry,
            options: {
              plugins: {
                title: {
                  display: true,
                  text: chart.title as string
                }
              },
              scales: chart.chartType === 'stack' ? scale : undefined
            },
            data: chart.chartData as any
          }
        ));
      }
    }
  }


  getReportInput() {
    this.getReportInputLoading = true;
    this._sub.sink = this.reportService.getReportFilter(this.reportId).subscribe(inputs => {
      this.reportFilters = inputs;
      for (const input of inputs) {
        if (input.type === 'enum' && input.required) {
          this.reportFilter[input.id] = input.extraParams[0].id;
        }
      }
      this.getReportInputLoading = false;
    }, (response: HttpError) => {
      if (response.error) {
        this.Toast.fire({ icon: 'error', text: response.error });
      }
      this.router.navigate(['report/general']);
    });
  }

  submitReportApi(ref?: any) {
    this.sendReportApiRequestLoading = true;
    this.reportService.sendUserApiResult(this.selectedReportApi.url, this.selectedReportApi.method, this.reportApiFilter).subscribe((response: any) => {
      if (ref) {
        ref.close();
      }
      if (response.data) {
        this.openJsonViewerDialog(response.data);
      } else {
        if (response.message) {
          this.Toast.fire({ icon: 'success', text: response.message });
        } else {
          this.Toast.fire({ icon: 'success', text: 'درخواست با موفقیت ارسال شد.' });
        }
      }

      this.getReportResult();
      this.sendReportApiRequestLoading = false;
    }, (response: HttpError) => {
      if (response.error) {
        this.sendReportApiRequestLoading = false;
        this.Toast.fire({ icon: 'error', text: response.error });
      }
    });
  }


  ngOnInit(): void {
    this.reportId = this.route.snapshot.params['id'];
    if (this.reportId) {
      this.getReportInput();
      this.getSingleReport();
      this.getReportOutput();
    } else {
      this.router.navigate(['report/general']);
    }
  }

  openReportApiModal(reportApi: ReportApiType, outputType: string) {
    this.selectedReportApi = reportApi;
    this.reportApiFilter = {};
    if (outputType === 'lazyResult' && reportApi.fields.length === 0) {
      this.sendReportApiRequestLoading = true;
      this.submitReportApi(undefined);
      return;
    }
    for (const filed of reportApi.fields) {
      this.reportApiFilter[filed.id] = filed.value;
    }
    this.dialogService.open(this.ReportApiFormModalTemplate);
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
    this.charts.map(chart => chart.destroy());
  }

}
